import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../../../lib/utils';

/** The shadcn/ui Item parts: one record of a list — a media tile, a title over a muted description, and
 *  actions on the trailing edge. Plain elements in shadcn too, so there is no Radix package behind them
 *  beyond `Slot` for `asChild`.
 *
 *  The anatomy and the `variant`/`size` axes are shadcn's. What is ours is the styling, read from this
 *  app's tokens, and two deliberate deviations:
 *  - `ItemGroup` separates its records with a GAP rather than with rules. A list of settings records is
 *    a stack of calm surfaces.
 *  - the `outline` and `muted` records answer the pointer with a quiet wash, so a list reads as live
 *    without any record pretending to be a button.
 *  - a record is a `listitem`, because its group is a `list`: shadcn gives the group the role and leaves
 *    its children without one, which a screen reader reports as a list of nothing. */

function ItemGroup({ className, ...props }: React.ComponentProps<'div'>) {
  return <div role="list" data-slot="item-group" className={cn('group/item-group flex flex-col gap-2', className)} {...props} />;
}

const itemVariants = cva(
  'group/item flex min-w-0 flex-wrap items-center rounded-lg border border-transparent text-ui outline-none ' +
    'transition-[background-color,border-color] duration-[var(--motion-fast)] ' +
    'focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 [a]:hover:bg-accent/50',
  {
    variants: {
      variant: {
        default: 'bg-transparent',
        outline: 'border-hairline bg-raised hover:bg-accent/40',
        muted: 'bg-sunken hover:bg-muted',
        selectable: 'cursor-pointer hover:bg-accent/40 has-[:focus-visible]:border-ring has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring/50 data-[selected=true]:border-primary/40 data-[selected=true]:bg-primary/5 data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-60',
      },
      size: {
        default: 'gap-4 p-4',
        sm: 'gap-3 px-3.5 py-3',
        // Ours, not shadcn's: a one-line record (a glyph, a line of text, a trailing action) that must stay
        // as low as the line it carries, such as a queued message above the composer.
        xs: 'gap-2 px-2.5 py-1.5',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  },
);

function Item({
  className,
  variant = 'default',
  size = 'default',
  asChild = false,
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof itemVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : 'div';
  return (
    <Comp
      role="listitem"
      data-slot="item"
      data-variant={variant}
      data-size={size}
      className={cn(itemVariants({ variant, size }), className)}
      {...props}
    />
  );
}

/** `icon` is the soft tile: a glyph or a brand mark in a hairline square on the muted ground. */
const itemMediaVariants = cva(
  'flex shrink-0 items-center justify-center gap-2 group-has-[[data-slot=item-description]]/item:self-start [&_svg]:pointer-events-none',
  {
    variants: {
      variant: {
        default: 'bg-transparent',
        icon: "size-9 rounded-lg border border-hairline bg-muted text-muted-foreground [&_svg:not([class*='size-'])]:size-4",
      },
    },
    defaultVariants: { variant: 'default' },
  },
);

function ItemMedia({ className, variant = 'default', ...props }: React.ComponentProps<'div'> & VariantProps<typeof itemMediaVariants>) {
  return <div data-slot="item-media" data-variant={variant} className={cn(itemMediaVariants({ variant }), className)} {...props} />;
}

function ItemContent({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="item-content" className={cn('flex min-w-0 flex-1 flex-col gap-1 [&+[data-slot=item-content]]:flex-none', className)} {...props} />;
}

function ItemTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="item-title" className={cn('flex w-fit max-w-full min-w-0 items-center gap-2 text-card-title tracking-ui', className)} {...props} />;
}

function ItemDescription({ className, ...props }: React.ComponentProps<'p'>) {
  return (
    <p
      data-slot="item-description"
      className={cn(
        'line-clamp-2 text-meta tracking-ui text-muted-foreground',
        'inline-links',
        className,
      )}
      {...props}
    />
  );
}

function ItemActions({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="item-actions" className={cn('flex shrink-0 items-center gap-2', className)} {...props} />;
}

export { Item, ItemActions, ItemContent, ItemDescription, ItemGroup, ItemMedia, ItemTitle };
